import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ArrowRightCircleIcon, MinusCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PhoneStatus, RequestStatus } from '@medflex/shared-types';
import useClassNames from '@medflex/hooks/useClassNames';

export const KINDS = {
  PRIMARY: 'primary',
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
};

const kinds = {
  [KINDS.PRIMARY]: 'border-blue-300 bg-blue-100 text-blue-800',
  [KINDS.ERROR]: 'border-red-300 bg-red-100 text-red-800',
  [KINDS.WARNING]: 'border-orange-300 bg-orange-100 text-orange-800',
  [KINDS.SUCCESS]: 'bg-green-100 border-green-300 text-green-900',
};

const getConfiguration = (phoneStatus, isForwardedCall) => {
  if (isForwardedCall) {
    return { translationKey: 'call.forwarded', icon: ArrowRightCircleIcon, kind: KINDS.PRIMARY };
  }

  if (phoneStatus === PhoneStatus.UNCOMPLETED) {
    return { translationKey: 'call.uncompleted', icon: MinusCircleIcon, kind: KINDS.WARNING };
  }

  if (phoneStatus === PhoneStatus.CANCELED) {
    return { translationKey: 'call.canceled', icon: XCircleIcon, kind: KINDS.ERROR };
  }

  return { translationKey: 'closed', icon: CheckCircleIcon, kind: KINDS.SUCCESS };
};

const RequestInfoBanner = ({ onClick, disabled, requestStatus, phoneStatus, isForwardedCall }) => {
  const { t } = useTranslation('messenger');
  const {
    translationKey,
    icon: IconComponent,
    kind,
  } = getConfiguration(phoneStatus, isForwardedCall);

  const cn = useClassNames(
    () => [
      'flex',
      'flex-col',
      'items-center',
      'text-center',
      'rounded-lg',
      'border-1',
      'px-4',
      'sm:px-16',
      'py-3',
      'gap-3',
      'max-w-2xl',
      kinds[kind],
    ],
    [kinds, kind],
  );

  return (
    <div
      data-component="RequestInfoBanner"
      className="w-full flex flex-col items-center mb-4 px-4 sm:px-20"
    >
      <div className={cn}>
        <div className="text-lg font-semibold inline-flex items-center gap-1 truncate">
          {IconComponent && <IconComponent className="w-5 h-5" />}
          {t(`RequestInfoBanner.${[translationKey]}.title`)}
        </div>
        <div>
          {!disabled
            ? t(`RequestInfoBanner.${[translationKey]}.add_message`)
            : t(`RequestInfoBanner.add_message_not_possible`)}
          {!disabled && requestStatus === RequestStatus.CLOSED && !isForwardedCall && (
            <button type="button" className="text-blue-500" onClick={onClick}>
              {t('RequestInfoBanner.add_message_button')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

RequestInfoBanner.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  requestStatus: PropTypes.oneOf(Object.values(RequestStatus)),
  phoneStatus: PropTypes.oneOf(Object.values(PhoneStatus)),
  isForwardedCall: PropTypes.bool,
};

export default RequestInfoBanner;
